/*******************************************************************************************************
   Copyright 2020 - Calise Partners LLC d/b/a Imaginuity. All rights are reserved.
   Reproduction or transmission in whole or in part, in any form or by any means, electronic, mechanical
or otherwise, is prohibited without the prior written consent of Calise Partners LLC d/b/a Imaginuity.
********************************************************************************************************
   Project : 10618 Maverick Platform Development
   Author : Imaginuity Developers (Kellan Been, Joe Bannon)
   Description : Provides custom styles and appends them to the head tag.
   Creation Date : Wed Nov 18 2020
   Documentation : [documentation url - point to default wiki for project if no specific documentation for script]
********************************************************************************************************/

import React, { useContext } from "react";
// HELPERS.
import { pxToRem, fontPercentage } from 'src/helpers';
import { addGTM, addStylesheet } from 'src/hooks';
// SCSS.
import "src/scss/vendors/kipsu.scss";

// THE STYLES AND FONT LINKS IN THIS FILE ARE NOW ONLY ADDED WHEN RUNNING LOCALLY, THEY GET ADDED OTHERWISE VIA SSR, SEE: server > styles

const Start = ({ subsite, principal, settings }) => {
    let params = new URLSearchParams(document.location.search);
    // Add classname for Approach specific custom css
    var domain = window.location.hostname.toLowerCase();
    var ssrStyles = document.getElementById('ssr-styles').innerHTML ? true : false;
    // GTM (Google Tag Manager)
    if (settings?.seo || principal?.settings?.seo) {
        const gtmCode = (settings?.seo?.google_tag_manager_id) ? settings?.seo?.google_tag_manager_id : principal?.settings?.seo?.google_tag_manager_id ? principal?.settings?.seo?.google_tag_manager_id : false;
        if (gtmCode) {
            addGTM(gtmCode.replace(/\s+/g, ''), params.get('mvk_skip_gtm_load'));
        }
    }

    // Breakpoints were not used on all components 
    // => future styled components should try to follow these
    const breakpoints = { // building theme object
        xs: `0px`,
        sm: `640px`,
        md: `1024px`,
        lg: `1280px`,
        xl: `1500px`
    }

    // FONTS.
    const fonts = { // building theme object
        heading: '',
        body: ''
    };
    if (settings?.design?.fonts && (settings?.design?.fonts?.heading?.heading_google_font_link || settings?.design?.fonts?.body?.body_google_font_link)) {
        // CACHING.
        cache.fonts.header = together(settings?.design?.fonts?.heading?.heading_google_font_name, settings?.design?.fonts?.heading?.heading_font_properties);
        cache.fonts.body = together(settings?.design?.fonts?.body?.body_google_font_name, settings?.design?.fonts?.body?.body_font_properties);
        if (!ssrStyles) {
            // HEADING FONT.
            addStylesheet(settings?.design?.fonts?.heading?.heading_google_font_link)
            // BODY FONT.
            addStylesheet(settings?.design?.fonts?.body?.body_google_font_link)
        }
    }

    // // FAVICON
    // if (settings?.branding?.favicon?.url) {
    //     var favicon = document.createElement('link');
    //     favicon.setAttribute('href', settings.branding?.favicon.url);
    //     favicon.setAttribute('rel', 'shortcut icon');
    //     document.getElementsByTagName('head')[0].appendChild(favicon);
    // }

    // COLORS
    const colors = { // building theme object
        primary: settings?.design?.colors?.primary_color,
        secondary: settings?.design?.colors?.secondary_color,
        tertiary: settings?.design?.colors?.tertiary_color,
        background: settings?.design?.colors?.background_color,
        bodyCopy: settings?.design?.colors?.body_copy_color,
        white: '#fff',
        colorSpash: '',
        seperatorLineColor: ''
    }

    // Layout: site max-width - offset 
    let restrictedWidth = Math.round(settings?.mvk_theme_config?.layout?.site_max_width * (100 - (settings?.mvk_theme_config?.layout?.restrict_max_width_offset ? settings?.mvk_theme_config?.layout?.restrict_max_width_offset : 17)) / 100);

    var StyleSheet = document.createElement('style');
    StyleSheet.setAttribute('id', 'dynamic-styles');

    var StylesString = ` .h1, .h2, .h3, .h4, .h5, .h6, h1, h2, h3, h4, h5, h6 { font-family: ${cache.fonts.header}; font-weight: ${settings?.design?.fonts?.heading?.heading_font_weight}; } `;
    StylesString += ` body { color: ${settings?.design?.colors?.body_copy_color}; background-color:${settings?.mvk_theme_config?.other?.body_background_color ?? '#fff'}; font-family:${cache.fonts.body}; font-weight: ${settings?.design?.fonts?.body?.body_font_weight}; } `;
    StylesString += ` a { color: ${settings?.design?.colors?.secondary_color}; } `;
    StylesString += ` body .grid-container { max-width: ${settings?.mvk_theme_config?.layout?.site_max_width ? settings?.mvk_theme_config?.layout?.site_max_width : '75'}rem }`;
    StylesString += ` body .grid-container.restricted { max-width: ${restrictedWidth}rem }`;

    StylesString += ` .primary-txt { color: ${settings?.design?.colors?.primary_color}; } `;
    StylesString += ` .secondary-txt { color: ${settings?.design?.colors?.secondary_color}; } `;
    StylesString += ` .tertiary-txt { color: ${settings?.design?.colors?.tertiary_color}; } `;
    StylesString += ` .background-txt { color: ${settings?.design?.colors?.background_color}; } `;
    StylesString += ` .body-copy-txt { color: ${settings?.design?.colors?.body_copy_color} !important;} `;
    StylesString += ` .white-txt {color: #ffffff !important; } `;

    StylesString += ` .primary-bg { background-color: ${settings?.design?.colors?.primary_color}; } `;
    StylesString += ` .secondary-bg { background-color: ${settings?.design?.colors?.secondary_color}; } `;
    StylesString += ` .tertiary-bg { background-color: ${settings?.design?.colors?.tertiary_color}; } `;
    StylesString += ` .background-bg { background-color: ${settings?.design?.colors?.background_color}; } `;
    StylesString += ` .body-copy-bg { background-color: ${settings?.design?.colors?.body_copy_color}; } `;
    StylesString += ` .white-bg { background-color: #fff; } `;

    StylesString += ` .primary-fill { fill: ${settings?.design?.colors?.primary_color}; } `;
    StylesString += ` .secondary-fill { fill: ${settings?.design?.colors?.secondary_color}; } `;
    StylesString += ` .tertiary-fill { fill: ${settings?.design?.colors?.tertiary_color}; } `;
    StylesString += ` .background-fill { fill: ${settings?.design?.colors?.background_color}; } `;

    //border colors
    StylesString += ` .primary-border { border-color: ${settings?.design?.colors?.primary_color}; } `;
    StylesString += ` .secondary-border { border-color: ${settings?.design?.colors?.secondary_color}; } `;
    StylesString += ` .tertiary-border { border-color: ${settings?.design?.colors?.tertiary_color}; } `;
    StylesString += ` .background-border { border-color: ${settings?.design?.colors?.background_color}; } `;
    StylesString += ` .body-copy-border { border-color: ${settings?.design?.colors?.body_copy_color}; } `;
    StylesString += ` .white-border { border-color: #fff !important; } `;

    // box shadow styles
    StylesString += ` .drop-shadow-light:hover { box-shadow: 5px 5px 15px rgba(0, 0, 0, .3) !important; }`;
    StylesString += ` .drop-shadow-medium:hover { box-shadow: 7px 7px 17px rgba(0, 0, 0, .5) !important; }`;
    StylesString += ` .drop-shadow-heavy:hover { box-shadow: 9px 9px 19px rgba(0, 0, 0, .7) !important; }`;

    // search and filter - no results styles
    StylesString += ` #noresults { margin: 9.375rem 0; display: flex; flex-flow: column nowrap; align-items: center; }`;
    StylesString += ` #noresults .title { width: fit-content; margin-bottom: 1.25rem; font-size: 1.625rem; }`;
    StylesString += ` #noresults .showfull { with: fit-content; text-decoration: underline; cursor: pointer; font-size: 1rem; }`;

    StylesString += ` #header .hover a, #header .hover div { color: ${settings?.design?.colors?.main_nav?.nav_txt_color}; } `;
    StylesString += ` #header .hover:hover a, #header .hover:hover div { color: ${settings?.design?.colors?.main_nav?.nav_txt_hv_color}; } `;
    StylesString += ` .button.tertiary { margin:0px; padding:10px; color: ${settings?.design?.colors?.body_copy_color} !important; background-color: ${settings?.design?.colors?.tertiary_color}; border-radius:${settings?.mvk_theme_config?.other?.border_radius_size}px; } `;
    StylesString += ` .hero .slick-dots li.slick-active button:before { background-color: ${settings?.design?.colors?.primary_color}; } `;
    StylesString += ` .image-carousel-hero__module .slick-slider.multiple-slides .slick-dots li.slick-active button:before { background-color: ${settings?.design?.colors?.primary_color} !important; } `;

    StylesString += ` .mvk-responsive-video { position: relative; height: 0; }`;
    StylesString += ` .mvk-responsive-video iframe { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }`;
    StylesString += ` .mvk-responsive-video.vertical { padding-bottom: 177.777%; }`;
    StylesString += ` .mvk-responsive-video.square { padding-bottom: 100%; }`;
    StylesString += ` .mvk-responsive-video.standard { padding-bottom: 75%; }`;
    StylesString += ` .mvk-responsive-video.widescreen { padding-bottom: 56.25%; }`;
    StylesString += ` .mvk-responsive-video.cinema { padding-bottom: 42.55%; }`;

    // related store name
    StylesString += `.post-type-list.three_col_image .related-store { font-family: ${cache.fonts.header};}`

    // Color Splashes
    const cs_color = settings?.mvk_theme_config?.other?.cs_color;
    let hr_color = '#000';

    switch (cs_color) {
        case 'primary-bg':
            hr_color = settings?.design?.colors?.primary_color;
            break;
        case 'secondary-bg':
            hr_color = settings?.design?.colors?.secondary_color;
            break;
        case 'tertiary-bg':
            hr_color = settings?.design?.colors?.tertiary_color;
            break;
        case 'custom':
            hr_color = settings?.mvk_theme_config?.other?.cs_custom_color;
            break;
        default:
            break;
    }
    StylesString += ` .color-splash-hr { height: 5px; width: 100px; border: none; margin: 1rem 0; background-color: ${hr_color};}`;
    colors.colorSpash = hr_color;

    if (settings?.mvk_theme_config?.other?.enable_color_splashes) {
        StylesString += ` .color-splash-header { height: 8px; background-color: ${hr_color}}`;
    }

    // Header Bottom Border
    if (settings?.mvk_theme_config?.header?.enable_bottom_border) {
        StylesString += ` .header__bottom-border {height: ${settings?.mvk_theme_config?.header?.border_height}; background-color: ${settings?.mvk_theme_config?.header?.border_color}; width: 100%;}`;
    }

    // Footer Bottom Border
    if (settings?.mvk_theme_config?.footer?.enable_footer_bottom_border) {
        StylesString += ` .footer__bottom-border {height: ${settings?.mvk_theme_config?.footer?.footer_border_height}; background-color: ${settings?.mvk_theme_config?.footer?.bottom_border_color}; width: 100%;}`;
    }

    // Separator Line Color - Content Area
    const option = settings?.mvk_theme_config?.other?.separator_line_color;
    var lineColor;
    switch (option) {
        case 'primary_color':
            var lineColor = `${settings?.design?.colors?.primary_color}, ${settings?.design?.colors?.primary_color}`;
            break;
        case 'secondary_color':
            var lineColor = `${settings?.design?.colors?.secondary_color}, ${settings?.design?.colors?.secondary_color}`;
            break;
        case 'tertiary_color':
            var lineColor = `${settings?.design?.colors?.tertiary_color}, ${settings?.design?.colors?.tertiary_color}`;
            break;
        case 'background_color':
            var lineColor = `${settings?.design?.colors?.background_color}, ${settings?.design?.colors?.background_color}`;
            break;
        case 'gradient':
            var lineColor = `${settings?.design?.colors?.gradient_color_1}, ${settings?.design?.colors?.gradient_color_2}`;
            break;
        default:
            break;
    }

    colors.seperatorLineColor = lineColor;

    StylesString += ` @media screen and (max-width: 639px) {
        .content-area .separator-lines:after {
            content: "";
            position: absolute;
            bottom: 0;
            left: 0;
            display: block;
            width: 100%;
            height: 2px;
            background: linear-gradient(to right, ${lineColor});
        } 
    }`;

    StylesString += ` @media screen and (min-width: 640px) {
        .content-area .separator-lines:after {
            content: "";
            position: absolute;
            top: 0;
            right: 0;
            display: block;
            width: 2px;
            height: 100%;
            background: linear-gradient(to bottom, ${lineColor});
        } 
    }`;

    StylesString += ` hr:not(.color-splash-hr) { background: linear-gradient(to right, ${lineColor}); height: 1px; border: transparent; }`;

    if (settings?.mvk_theme_config?.layout?.mobile_content_margins) {
        StylesString += ` @media (max-width: 639px) {#modules-container .grid-container:not(.full) { padding-left: ${settings?.mvk_theme_config?.layout?.mobile_content_margins}rem; padding-right: ${settings?.mvk_theme_config?.layout?.mobile_content_margins}rem; }}`
    }
    if (settings?.mvk_theme_config?.layout?.tablet_content_margins) {
        StylesString += ` @media (min-width: 640px) {#modules-container .grid-container:not(.full) { padding-left: ${settings?.mvk_theme_config?.layout?.tablet_content_margins}rem; padding-right: ${settings?.mvk_theme_config?.layout?.tablet_content_margins}rem; }}`
    }

    if (settings?.design?.advanced?.font_customizations) {
        if (settings?.design?.fonts?.heading_google_font_select || settings?.design?.fonts?.body_google_font_select) {
            let fontUrl = `https://fonts.googleapis.com/css?family=`;

            if (settings?.design?.fonts?.heading_google_font_select) {
                fontUrl += settings?.design?.fonts?.heading_google_font_select.label;
                if (settings?.design?.fonts?.heading_font_variants) {
                    let variants = '';
                    settings?.design?.fonts?.heading_font_variants.forEach((variant, index, array) => {
                        variants += `${variant}${index !== array.length - 1 ? ',' : ''}`
                    });
                    fontUrl += `:${variants}`;
                }
                cache.fonts.header = settings?.design?.fonts?.heading_google_font_select.value;
                StylesString += `.h1, .h2, .h3, .h4, .h5, .h6, h1, h2, h3, h4, h5, h6 { font-family: ${settings?.design?.fonts?.heading_google_font_select.value};}`
            }

            if (settings?.design?.fonts?.body_google_font_select) {
                fontUrl += `|${settings?.design?.fonts?.body_google_font_select.label}`;
                if (settings?.design?.fonts?.body_font_variants) {
                    let variants = '';
                    settings?.design?.fonts?.body_font_variants.forEach((variant, index, array) => {
                        variants += `${variant}${index !== array.length - 1 ? ',' : ''}`
                    });
                    fontUrl += `:${variants}`;
                }
                cache.fonts.body = settings?.design?.fonts?.body_google_font_select.value;
                StylesString += `body { font-family: ${settings?.design?.fonts?.body_google_font_select.value};}`
            }
            fontUrl += `&display=swap`;
            if (!ssrStyles) {
                addStylesheet(fontUrl);
            }
        }
        const fontStyles = settings?.design?.advanced;
        StylesString += `#app #header #dropdown, #app #header #dropdown a, #app #header nav a, #app #header nav .menu-dropdown { font-family: ${fontStyles.nav?.font_family === 'heading' ? cache.fonts.header : cache.fonts.body}; font-size: ${pxToRem(fontStyles.nav?.size_small)}rem; font-weight: ${fontStyles.nav?.weight}; letter-spacing: ${fontStyles.nav?.letter_spacing}px; }`
        StylesString += `#app #head-utility, #app #head-utility a, #app #head-utility p { font-family: ${fontStyles.utility?.font_family === 'heading' ? cache.fonts.header : cache.fonts.body}; font-size: ${pxToRem(fontStyles.utility?.size_small)}rem; font-weight: ${fontStyles.utility?.weight}; letter-spacing: ${fontStyles.utility?.letter_spacing}px; }`
        StylesString += `#app h1 { font-size: ${pxToRem(fontStyles.h1?.size_small)}rem; font-weight: ${fontStyles.h1?.weight}; line-height: ${fontStyles.h1?.line_height}; letter-spacing: ${fontStyles.h1?.letter_spacing}px; }`
        StylesString += `#app h2 { font-size: ${pxToRem(fontStyles.h2?.size_small)}rem; font-weight: ${fontStyles.h2?.weight}; line-height: ${fontStyles.h2?.line_height}; letter-spacing: ${fontStyles.h2?.letter_spacing}px; }`
        StylesString += `#app h3 { font-size: ${pxToRem(fontStyles.h3?.size_small)}rem; font-weight: ${fontStyles.h3?.weight}; line-height: ${fontStyles.h3?.line_height}; letter-spacing: ${fontStyles.h3?.letter_spacing}px; }`
        StylesString += `#app h4 { font-size: ${pxToRem(fontStyles.h4?.size_small)}rem; font-weight: ${fontStyles.h4?.weight}; line-height: ${fontStyles.h4?.line_height}; letter-spacing: ${fontStyles.h4?.letter_spacing}px; }`
        StylesString += `#app h5 { font-size: ${pxToRem(fontStyles.h5?.size_small)}rem; font-weight: ${fontStyles.h5?.weight}; line-height: ${fontStyles.h5?.line_height}; letter-spacing: ${fontStyles.h5?.letter_spacing}px; }`
        StylesString += `#app h6 { font-size: ${pxToRem(fontStyles.h6?.size_small)}rem; font-weight: ${fontStyles.h6?.weight}; line-height: ${fontStyles.h6?.line_height}; letter-spacing: ${fontStyles.h6?.letter_spacing}px; }`
        StylesString += `#app a, #app button { font-size: ${pxToRem(fontStyles.links?.size_small)}rem; font-weight: ${fontStyles.links?.weight}; line-height: ${fontStyles.links?.line_height}; letter-spacing: ${fontStyles.links?.letter_spacing}px; }`
        StylesString += `#app p, #app ol, #app ul { font-size: ${pxToRem(fontStyles.paragraph?.size_small)}rem; font-weight: ${fontStyles.paragraph?.weight}; line-height: ${fontStyles.paragraph?.line_height}; letter-spacing: ${fontStyles.paragraph?.letter_spacing}px; }`
        StylesString += `#app .location-selector a, #app .location-selector p { font-size: ${fontPercentage(fontStyles?.paragraph?.size_small, 81)}rem;}`
        // > 768px
        StylesString += `@media (min-width: 768px) { 
            #app h1 { font-size: ${pxToRem(fontStyles.h1?.size_large)}rem;}
            #app h2 { font-size: ${pxToRem(fontStyles.h2?.size_large)}rem;}
            #app h3 { font-size: ${pxToRem(fontStyles.h3?.size_large)}rem;}
            #app h4 { font-size: ${pxToRem(fontStyles.h4?.size_large)}rem;}
            #app h5 { font-size: ${pxToRem(fontStyles.h5?.size_large)}rem;}
            #app h6 { font-size: ${pxToRem(fontStyles.h6?.size_large)}rem;}
            #app a, #app button { font-size: ${pxToRem(fontStyles.links?.size_large)}rem;}
            #app p, #app ol, #app ul { font-size: ${pxToRem(fontStyles.paragraph?.size_large)}rem;}

        }`
        // > 1200px 
        StylesString += `@media (min-width: 1200px) {
            #app #header #nav-expanded,
            #app #header #nav-expanded a,
            #app #header nav a, #app #header nav .menu-dropdown,
            #app #header #style-one #nav-expanded .nav-primary-items .nav-primary-item .nav-primary-item-main .nav-primary-item-main-title { font-family: ${fontStyles.nav?.font_family === 'heading' ? cache.fonts.header : cache.fonts.body}; font-size: ${pxToRem(fontStyles.nav?.size_large)}rem; font-weight: ${fontStyles.nav?.weight}; letter-spacing: ${fontStyles.nav?.letter_spacing}px; }
            #app #head-utility, #app #head-utility a, #app #head-utility p { font-size: ${pxToRem(fontStyles.utility?.size_large)}rem;}
            #app .location-selector a, #app .location-selector p { font-size: ${fontPercentage(fontStyles?.paragraph?.size_large, 81)}rem; }
        }`
    }


    var bodyEl = document.getElementById('body');

    if (domain.includes('approach')) {
        bodyEl.classList.add('approach');
    }

    // custom CSS - Super Config > Advanced
    StylesString += settings?.design?.advanced?.custom_css ? settings?.design?.advanced?.custom_css : '';

    StyleSheet.innerHTML = StylesString;

    const isStyleSheetAlreadyLoaded = document.contains(document.getElementById('dynamic-styles'));
    if (isStyleSheetAlreadyLoaded) {// remove stylesheet | Prevents duplicates, and allows styles to be reset
        document.getElementById('dynamic-styles').remove();
    }

    if (!ssrStyles) {
        document.getElementsByTagName('head')[0].appendChild(StyleSheet);
    }

    const theme = { // Theme object for StyledComponents
        breakpoints: breakpoints,
        colors: colors,
        fonts: fonts
    }

    return (theme);
};

export default Start;

function together(font, props) {
    var stuff = `'${font}'`;
    if (props?.length > 0) {
        stuff = stuff.concat(', ', props.join());
    } else {
        stuff = stuff + ', sans-serif'
    }
    return stuff;
}
